import {
  FaCheckCircle,
  FaBan,
  FaExclamationTriangle,
  FaHourglassHalf,
  FaFileInvoiceDollar,
} from 'react-icons/fa';
import { InvoiceStatus } from '../../types';

interface StatusBadgeProps {
  status: InvoiceStatus;
  size?: 'sm' | 'md' | 'lg';
  clickable?: boolean;
  onClick?: () => void;
}

export const getStatusIcon = (
  status: InvoiceStatus,
  size?: number,
  monochrome?: boolean,
) => {
  const iconSize = size || 12;

  // If monochrome is true, don't set color (inherit from parent)
  const getIconColor = (defaultColor: string) =>
    monochrome ? undefined : defaultColor;

  switch (status) {
    case InvoiceStatus.PAID:
      return (
        <FaCheckCircle
          color={getIconColor('var(--mantine-color-gray-7)')}
          size={iconSize}
        />
      );
    case InvoiceStatus.PARTIALLY_PAID:
      return (
        <FaExclamationTriangle
          color={getIconColor('var(--mantine-color-gray-7)')}
          size={iconSize}
        />
      );
    case InvoiceStatus.CONFIRMED_BY_CLIENT:
      return (
        <FaHourglassHalf
          color={getIconColor('var(--mantine-color-gray-7)')}
          size={iconSize}
        />
      );
    case InvoiceStatus.ISSUED:
      return (
        <FaFileInvoiceDollar
          color={getIconColor('var(--mantine-color-gray-6)')}
          size={iconSize}
        />
      );
    case InvoiceStatus.CANCELLED:
      return (
        <FaBan
          color={getIconColor('var(--mantine-color-gray-7)')}
          size={iconSize}
        />
      );
    default:
      return null;
  }
};

export const getStatusColor = (status: InvoiceStatus) => {
  switch (status) {
    case InvoiceStatus.PAID:
      return 'green';
    case InvoiceStatus.PARTIALLY_PAID:
      return 'orange';
    case InvoiceStatus.CONFIRMED_BY_CLIENT:
      return 'blue';
    case InvoiceStatus.ISSUED:
      return 'gray';
    case InvoiceStatus.CANCELLED:
      return 'red';
    default:
      return 'gray';
  }
};

export const getStatusLabel = (status: InvoiceStatus) => {
  switch (status) {
    case InvoiceStatus.PAID:
      return 'Paid';
    case InvoiceStatus.PARTIALLY_PAID:
      return 'Partially Paid';
    case InvoiceStatus.CONFIRMED_BY_CLIENT:
      return 'Client Confirmed';
    case InvoiceStatus.ISSUED:
      return 'Issued';
    case InvoiceStatus.CANCELLED:
      return 'Cancelled';
    default:
      return status;
  }
};

export function StatusBadge({
  status,
  size = 'md',
  clickable = false,
  onClick,
}: StatusBadgeProps) {
  const iconSize = size === 'sm' ? 12 : size === 'lg' ? 16 : 14;

  return (
    <div
      onClick={clickable ? onClick : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        cursor: clickable ? 'pointer' : 'default',
        fontSize: size === 'sm' ? '12px' : size === 'lg' ? '16px' : '14px',
        color: getStatusColor(status),
      }}
    >
      {getStatusIcon(status, iconSize)}
      <span>{getStatusLabel(status)}</span>
    </div>
  );
}

export default StatusBadge;
